import type { ListTenantsSort, SortDirection, TenantSummary } from '@t4s/api-client/model'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ApiFailure } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { useDocumentTitle } from '../shell/document-title.ts'
import { Notice } from '../shell/notice.tsx'
import { fetchTenants, TENANTS_QUERY_KEY } from './gateway.ts'
import { formatDate, ownerTimeZone } from './instants.ts'
import {
  type ResolvedTenantList,
  resolveTenantListSearch,
  type TenantListSearch,
  type TenantStateFilter,
  tenantCountLine,
  tenantListSearchFrom,
} from './list-search.ts'
import { LOCALE_LABELS } from './locales.ts'

/**
 * The tenant list — docs/ux/PLATFORM-ADMIN.md §7.
 *
 * The estate, in a real `<table>`: never virtualised, never scrolling the page sideways, always
 * paged. Find-in-page works, and that is a feature of being plain (§5.3).
 *
 * Two things it does that a list usually does not:
 *
 * - **The search and the state filter are server-side** (§7.2). A client-side filter over
 *   one page of an unpaged estate is the old admin screen's mistake, and it lies as soon as
 *   there is a second page.
 * - **A live row says when nobody can get into that tenant** (§7.4). Zero accounts, or every
 *   account pending or disabled, is a company that cannot fix itself — minting an invite is
 *   an admin action *inside* the tenant, and there is no admin. It is surfaced here rather
 *   than left to be discovered, and the tenant's page has the control that fixes it.
 *   **Live rows only**: a retired tenant is unenterable deliberately and has no way back
 *   in, so the marker would be a promise this application cannot keep. Its badge says
 *   Retired, which is the explanation.
 *
 * Every instant on this screen is a **platform** record and is therefore in the platform
 * owner's own zone, labelled (§7.5).
 */

/** The page size, from `packages/domain`'s shared pagination. */
const PAGE_SIZE = 25

/**
 * How long the search field settles before the table refetches (§7.2).
 *
 * Exported because a test asserting on this window has to know how wide it is, the same
 * way `handoff.tsx` exports its reveal timeout. A test that hard-coded 250 would keep
 * passing, and stop meaning anything, the day this number moved.
 */
export const SEARCH_DEBOUNCE_MS = 250

export type TenantListScreenProps = {
  /**
   * The list's state, from the address bar (§7.2). Every key is optional and a default is absent:
   * `{}` is the plain list — live tenants, by name, page one.
   *
   * `q` is also §20.4's lookup and §8.4's rescue. `rescue` is true when the owner arrived here from
   * a create that half-succeeded: the tenant exists and nobody can sign in to it — either it has
   * no administrator, or the one it has has no activation link. The list is filtered to its slug
   * so the owner can open it and use §11.3's controls, which is where both repairs live and where
   * the two states are told apart. It is a search parameter rather than component state because
   * the navigation is what carries it, and because a reload while the owner is reading it must
   * not lose the explanation.
   */
  readonly search?: TenantListSearch
}

export function TenantListScreen({ search = {} }: TenantListScreenProps = {}) {
  useDocumentTitle('Tenants')
  const navigate = useNavigate({ from: '/tenants' })
  const searchId = useId()
  const stateId = useId()
  const zone = ownerTimeZone()

  const rescue = search.rescue === true
  const list = resolveTenantListSearch(search)
  const { q, state, sort, dir: direction } = list
  const pageNumber = list.page
  const offset = (pageNumber - 1) * PAGE_SIZE

  /**
   * Moves the address to the list with `change` applied over whatever the address holds *now*.
   *
   * Read at the moment of the call rather than captured when the handler was made, so a search
   * that settles after the owner has already changed the state filter lands on top of that
   * change instead of undoing it. Each call is one history entry unless it says otherwise (§7.2),
   * and `rescue` is not carried: the notice explains one arrival, and a list the owner has since
   * changed is no longer that arrival.
   */
  const go = useCallback(
    (change: Partial<ResolvedTenantList>, options: { replace?: boolean } = {}) => {
      void navigate({
        to: '/tenants',
        search: (previous: TenantListSearch) =>
          tenantListSearchFrom({ ...resolveTenantListSearch(previous), ...change }),
        ...(options.replace === true ? { replace: true } : {}),
      })
    },
    [navigate],
  )

  // What the field shows is what was typed; what the address holds is what was *settled* (§7.2).
  const [typed, setTyped] = useState(q)
  // The search this screen last put in the address, or last read from it.
  const knownQ = useRef(q)

  // The address moved under the field — Back, Forward, a link, Clear: the field follows it.
  useEffect(() => {
    if (q !== knownQ.current) {
      knownQ.current = q
      setTyped(q)
    }
  }, [q])

  // Debounced, so the table does not refetch — and history does not gain an entry — on every
  // keystroke (§7.2).
  //
  // **Nothing is pending while the typed text and the address agree, and that guard is
  // load-bearing.** Without it this effect armed a timer on mount as well, and a quarter of a
  // second later that timer sent the page back to the first over whatever had happened since — so
  // pressing Next inside the first 250ms of the list appearing silently returned the owner to
  // page one. Narrow, and real: the estate renders from cache on the way back from a tenant's
  // page, so the first quarter-second is not always the one nobody is looking at.
  useEffect(() => {
    const settled = typed.trim()
    if (settled === q) {
      return
    }
    const timer = setTimeout(() => {
      knownQ.current = settled
      // The new filter's first page, not the page the previous filter was on (§7.2).
      go({ q: settled, page: 1 })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [typed, q, go])

  const page = useQuery({
    queryKey: [...TENANTS_QUERY_KEY, { q, state, sort, direction, offset }],
    queryFn: () =>
      fetchTenants({
        limit: PAGE_SIZE,
        offset,
        state,
        sort,
        direction,
        ...(q === '' ? {} : { search: q }),
      }),
    // The visible rows stay and stay readable while a filter changes (§7.6): a table that
    // blanks on a keystroke of its own search is unreadable exactly while it is being used.
    placeholderData: keepPreviousData,
  })

  // "N of M tenants match" needs M, the whole estate, which no filtered read can say (§7.3).
  // Only a search needs it; the other forms of the count are complete without it.
  const searching = q !== ''
  const estate = useQuery({
    queryKey: [...TENANTS_QUERY_KEY, 'estate-size'],
    queryFn: () =>
      fetchTenants({ limit: 1, offset: 0, state: 'any', sort: 'name', direction: 'asc' }),
    select: (everything) => everything.total,
    enabled: searching,
  })

  const filtered = searching || state !== 'live'
  const items = page.data?.items ?? []
  const total = page.data?.total ?? 0
  // The pager describes the rows on screen. While the previous page's rows are held for a changed
  // page (`keepPreviousData`, §7.6) the address already names the new offset, so the range and the
  // Previous/Next states are read from the offset of the answer that holds those rows — the page's
  // own, in the response — not the address's. Otherwise Next briefly read "26–50 of 40" (N5).
  const shownOffset = page.isPlaceholderData && page.data !== undefined ? page.data.offset : offset

  // A page the estate no longer has — a bookmark from when it was larger, a hand-edited address —
  // is not an empty list, and must not read as "No tenants yet": it is replaced, in place so Back
  // does not return to it, by the last page that exists.
  useEffect(() => {
    if (
      page.isSuccess &&
      !page.isPlaceholderData &&
      items.length === 0 &&
      total > 0 &&
      offset > 0
    ) {
      go({ page: Math.ceil(total / PAGE_SIZE) }, { replace: true })
    }
  }, [page.isSuccess, page.isPlaceholderData, items.length, total, offset, go])

  const changeSort = (key: ListTenantsSort) => {
    if (sort === key) {
      go({ dir: direction === 'asc' ? 'desc' : 'asc', page: 1 })
    } else {
      go({ sort: key, dir: 'asc', page: 1 })
    }
  }

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Tenants</h1>
          {/*
            The count, and what it is a count of (§7.3): it follows the filter, and used to say
            nothing of it (TAS-14, L1). A test asserts the exact text of each form.

            **Only for the list the address names.** While the previous list's rows stay on screen
            for a changed filter (`keepPreviousData`, §7.6) `page.data` is still that previous list,
            and its total under the new filter's name — "30 retired tenants" for the live count —
            would be the very mislabel L1 is about. The rows hold their place; the line says
            "Loading…" until its own answer is in.
          */}
          <p className="page-lead">
            {page.isSuccess && !page.isPlaceholderData
              ? tenantCountLine({
                  total,
                  searching,
                  state,
                  estateTotal: estate.isSuccess ? estate.data : undefined,
                })
              : 'Loading…'}
          </p>
        </div>
        {/* Stays usable when the read fails, so one failed read cannot trap the owner. */}
        <div className="page-header-actions">
          <button
            type="button"
            className="button button-primary"
            onClick={() => {
              void navigate({ to: '/tenants/new' })
            }}
          >
            Create a tenant
          </button>
        </div>
      </div>

      {rescue ? (
        <Notice tone="warning" assertive tag="Do not retry">
          That tenant was created and nobody can sign in to it yet: either its first administrator
          was not created, or their activation link was not. Open it below — its page shows which —
          and create a superadmin, or mint a link for the one that is listed. Do{' '}
          <strong>not</strong> create the tenant again: its slug is reserved permanently and a
          second attempt would be refused.
        </Notice>
      ) : null}

      <div className="filters">
        <div className="field">
          <label htmlFor={searchId}>Search name or slug</label>
          <input
            id={searchId}
            type="search"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value)
            }}
          />
        </div>
        <div className="field">
          <label htmlFor={stateId}>State</label>
          <select
            id={stateId}
            value={state}
            onChange={(event) => {
              go({ state: event.target.value as TenantStateFilter, page: 1 })
            }}
          >
            <option value="live">Live</option>
            <option value="retired">Retired</option>
            <option value="any">Both</option>
          </select>
        </div>
      </div>

      {page.isError ? (
        <div className="panel panel-error">
          <h2>The tenant list could not be loaded</h2>
          <Notice tone="failure">
            {messageFor(
              'tenantList',
              page.error instanceof ApiFailure ? page.error.code : 'client_unknown',
            )}
          </Notice>
          <button
            type="button"
            className="button"
            onClick={() => {
              void page.refetch()
            }}
          >
            Try again
          </button>
        </div>
      ) : null}

      <div className="table-frame">
        <table className="table table-stack table-sortable table-tenants">
          <caption className="visually-hidden">Every tenant on this deployment</caption>
          <thead>
            <tr>
              <SortHeader
                label="Name"
                column="name"
                sort={sort}
                direction={direction}
                onSort={changeSort}
              />
              <SortHeader
                label="Slug"
                column="slug"
                sort={sort}
                direction={direction}
                onSort={changeSort}
              />
              {/* Not controls that do nothing: these four are plain labels (§7.1). */}
              <th scope="col">State</th>
              <th scope="col">Users</th>
              <th scope="col">Timezone</th>
              <th scope="col">Languages</th>
              {/* Every instant on this surface is labelled with the zone it is in (§7.5). */}
              <SortHeader
                label={`Created · ${zone}`}
                column="createdAt"
                sort={sort}
                direction={direction}
                onSort={changeSort}
              />
            </tr>
          </thead>
          <tbody>
            {page.isPending
              ? Array.from({ length: 3 }, (_unused, index) => (
                  // Placeholder rows, never a centred spinner over an empty page (§18.1).
                  // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
                  <tr key={`placeholder-${index}`}>
                    <td colSpan={7}>
                      <span className="skeleton" />
                    </td>
                  </tr>
                ))
              : items.map((tenant) => (
                  <TenantRow key={tenant.tenantId} tenant={tenant} zone={zone} />
                ))}
          </tbody>
        </table>
      </div>

      {page.isSuccess && items.length === 0 ? (
        <EmptyState
          filtered={filtered}
          onClear={() => {
            // The plain address, `/tenants`: no parameter at all, and the typed text with it.
            setTyped('')
            knownQ.current = ''
            void navigate({ to: '/tenants', search: {} })
          }}
        />
      ) : null}

      {items.length > 0 ? (
        <nav className="pager" aria-label="Pages">
          <p className="num">
            {shownOffset + 1}–{shownOffset + items.length} of {total}
          </p>
          <div className="row">
            <button
              type="button"
              className="button button-sm"
              disabled={shownOffset === 0}
              onClick={() => {
                go({ page: pageNumber - 1 })
              }}
            >
              Previous
            </button>
            <button
              type="button"
              className="button button-sm"
              disabled={shownOffset + items.length >= total}
              onClick={() => {
                go({ page: pageNumber + 1 })
              }}
            >
              Next
            </button>
          </div>
        </nav>
      ) : null}
    </main>
  )
}

function SortHeader({
  label,
  column,
  sort,
  direction,
  onSort,
}: {
  readonly label: string
  readonly column: ListTenantsSort
  readonly sort: ListTenantsSort
  readonly direction: SortDirection
  readonly onSort: (column: ListTenantsSort) => void
}) {
  return (
    <th scope="col" aria-sort={ariaSort(sort, direction, column)}>
      <button
        type="button"
        className="sort"
        onClick={() => {
          onSort(column)
        }}
      >
        {label}
        {sort === column ? (direction === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  )
}

function TenantRow({ tenant, zone }: { tenant: TenantSummary; zone: string }) {
  const dim = tenant.state === 'retired' ? ' muted' : ''
  // Below 48 rem a row shows its name, slug, state and users; the rest is folded behind a button
  // (TAS-14, P4). Above it nothing is folded, the button is not drawn, and this state does nothing.
  const [open, setOpen] = useState(false)

  return (
    <tr {...(open ? { 'data-open': 'true' } : {})}>
      <td className="name col-name">
        <Link to="/tenants/$tenantId" params={{ tenantId: tenant.tenantId }}>
          {tenant.name}
        </Link>
        <button
          type="button"
          className="button button-sm row-fold"
          aria-expanded={open}
          aria-label={`Details of ${tenant.name}`}
          onClick={() => {
            setOpen(!open)
          }}
        >
          Details
        </button>
      </td>
      {/* Monospace, exactly as stored — it is a hostname, and a proportional font makes
          `rn` and `m` an interesting evening (§7.1). */}
      <td className="col-slug" data-label="Slug">
        <code>{tenant.slug}</code>
      </td>
      <td className="col-state" data-label="State">
        {/* A word, with its own styling, never a colour alone (§21). */}
        <span className={`badge badge-${tenant.state}`}>
          {tenant.state === 'live' ? 'Live' : 'Retired'}
        </span>
      </td>
      <td className="num col-users" data-label="Users">
        {tenant.users.total}
        {/* §7.4's marker means "this needs fixing, and §11.3 is the way back in". A
            retired tenant is unenterable *on purpose* and has no way back in, so the
            marker would be noise on every retired row — a retired tenant always answers
            `canSignIn: false` — and a promise this application cannot keep. The Retired
            badge in the previous column already says why nobody can sign in. Live only.
            A second line under the count, not a clause after it: the count is what the
            column is for, and a clause wrapped over four lines in a narrow cell (TAS-14, T1). */}
        {tenant.state === 'live' && !tenant.canSignIn ? (
          <span className="warning-marker users-note" title="Nobody in this tenant can sign in">
            nobody can sign in
          </span>
        ) : null}
      </td>
      {/* The IANA name, unabbreviated: `CET` is ambiguous twice a year (§7.1). */}
      <td className={`mono col-zone fold${dim}`} data-label="Timezone">
        {tenant.timeZone}
      </td>
      <td className="col-lang fold" data-label="Languages">
        {languagesOf(tenant)}
      </td>
      <td className={`num col-created fold${dim}`} data-label={`Created · ${zone}`}>
        {formatDate(tenant.createdAt, zone)}
      </td>
    </tr>
  )
}

/**
 * The languages as names, the default first and marked, then the rest in the order the API
 * sent them: "Deutsch (default), English". A code is what the API speaks; the owner speaks
 * the names (`locales.ts`).
 */
function languagesOf(tenant: TenantSummary): string {
  const others = tenant.enabledLocales.filter((locale) => locale !== tenant.defaultLocale)
  return [
    `${LOCALE_LABELS[tenant.defaultLocale]} (default)`,
    ...others.map((locale) => LOCALE_LABELS[locale]),
  ].join(', ')
}

/**
 * Empty is two different states, and they get two different sentences (§7.6, §18.1).
 *
 * The estate before the first company must read as an invitation rather than as a failure:
 * it is the first screen the owner ever sees.
 */
function EmptyState({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
  if (filtered) {
    return (
      <div className="panel">
        <h2>No tenant matches</h2>
        <p>A search or a state filter is narrowing this list.</p>
        <button type="button" className="button" onClick={onClear}>
          Clear the filters
        </button>
      </div>
    )
  }

  return (
    <div className="panel">
      <h2>No tenants yet</h2>
      <p>
        This is the estate before its first company. Creating one sets up its account, its languages
        and the first administrator who can sign in.
      </p>
      <Link className="button button-primary" to="/tenants/new">
        Create a tenant
      </Link>
    </div>
  )
}

function ariaSort(
  sort: ListTenantsSort,
  direction: SortDirection,
  column: ListTenantsSort,
): 'ascending' | 'descending' | 'none' {
  if (sort !== column) {
    return 'none'
  }
  return direction === 'asc' ? 'ascending' : 'descending'
}
